import * as React from 'react';
import { Dialog, DialogContent, DialogTitle, IconButton, TextFieldVariants } from '@mui/material';
import { renderHelpText } from './renderHelpText';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import HelpIcon from '@material-ui/icons/Help';
import { useState } from 'react';
import moment, { Moment } from 'moment';

export interface TimeFieldProps {
    value?: string | Date | Moment;
    id?: string;
    name?: string;
    disabled?: boolean;
    readOnly?: boolean;
    invalid?: boolean;
    placeholder?: string;
    label?: React.ReactNode;
    helpText?: string;
    description?: string;
    style?: React.CSSProperties;
    inputStyle?: React.CSSProperties;
    onChange?: (value: string) => void;
    className?: string;
    variant?: TextFieldVariants;
    minTime?: string | Date | Moment;
    maxTime?: string | Date | Moment;
    format?: string;
    ampm?: boolean;
    minutesStep?: number;
    views?: ('hours' | 'minutes' | 'seconds')[];
}

export const TimeField = (props: TimeFieldProps) => {
    const {
        value,
        id,
        name,
        disabled,
        readOnly,
        placeholder,
        label,
        helpText,
        description,
        invalid,
        style,
        inputStyle,
        onChange,
        className,
        variant = 'outlined',
        minTime,
        maxTime,
        format = 'HH:mm',
        ampm = false,
        minutesStep = 1,
        views = ['hours', 'minutes'],
    } = props;

    const [showHelp, setShowHelp] = useState(false);

    // Convert value to Moment object
    const momentValue = value ? moment(value, 'HH:mm') : null;

    // Handle time change
    const handleTimeChange = (time: Moment | null) => {
        if (onChange) {
            if (time && time.isValid()) {
                // Return time in HH:mm format
                onChange(time.format('HH:mm'));
            } else {
                onChange('');
            }
        }
    };

    return (
        <>
            <LocalizationProvider dateAdapter={AdapterMoment}>
                <TimePicker
                    value={momentValue}
                    onChange={handleTimeChange}
                    disabled={disabled || readOnly}
                    label={
                        helpText ? (
                            <div>
                                {label}{' '}
                                <IconButton size="small" onClick={() => setShowHelp(true)}>
                                    <HelpIcon />
                                </IconButton>
                            </div>
                        ) : (
                            label
                        )
                    }
                    minTime={minTime ? moment(minTime, 'HH:mm') : undefined}
                    maxTime={maxTime ? moment(maxTime, 'HH:mm') : undefined}
                    format={format}
                    ampm={ampm}
                    minutesStep={minutesStep}
                    views={views}
                    slotProps={{
                        textField: {
                            id: id ? `${id}-input` : undefined,
                            name,
                            className,
                            style,
                            placeholder,
                            helperText: description,
                            error: invalid,
                            variant,
                            size: 'small' as const,
                            fullWidth: true,
                            InputProps: {
                                style: inputStyle,
                            },
                        },
                    }}
                />
            </LocalizationProvider>

            {helpText?.length && (
                <Dialog open={showHelp} onClose={() => setShowHelp(false)}>
                    <DialogTitle>{label}</DialogTitle>
                    <DialogContent>{renderHelpText(helpText)}</DialogContent>
                </Dialog>
            )}
        </>
    );
};
